// Compact per-row actions kebab (⋮). Houses Edit / History / Delete in a
// portal dropdown so the row stays a single icon tall and the menu isn't
// clipped by the grid's horizontal scroll container. Mirrors the live
// preview's deploy 3-dot menu pattern.

import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal, MoreVertical } from 'lucide-react'

import { LayerLiftContext, Z_MENU_OVER_TRAIL, Z_ROW_MENU } from '../../layers'
import { tw } from '../../testHooks'

export interface RowAction {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  danger?: boolean
  /** What this item IS, for a surface with room to draw one of them as a
   *  button. Matching on the LABEL would break the moment a designer renamed
   *  the Edit system action, which is exactly what they are allowed to do. */
  kind?: 'run' | 'open' | 'assign' | 'alias' | 'trail' | 'history' | 'full' | 'delete'
  /** The test-hook name (``data-tw-action``): the model action's name for a
   *  run item, else the system verb. Falls back to ``kind``, then the label. */
  hook?: string
  /** The keyboard shortcut that runs it, as shown ("Alt+A"). */
  shortcut?: string
}

const menuKbd: React.CSSProperties = {
  fontFamily: 'inherit', fontSize: 12, color: 'var(--color-text-secondary, #465062)',
  border: '1px solid var(--color-border, #dde3ea)', borderRadius: 4, padding: '0 5px', whiteSpace: 'nowrap',
}

/** The ringed "⋮": quiet at rest, the accent while pointed at or open. */
function circledTrigger(open: boolean, hover: boolean): React.CSSProperties {
  const on = open || hover
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, padding: 0, borderRadius: '50%', flex: 'none', cursor: 'pointer',
    verticalAlign: 'middle',
    border: `1px solid ${on ? 'var(--color-accent, #0061D0)' : 'var(--color-border-strong, #c7cfd9)'}`,
    background: on ? 'var(--color-accent-weak, rgba(0,97,208,0.10))' : 'var(--color-bg-secondary, #ffffff)',
    color: on ? 'var(--color-accent, #0061D0)' : 'var(--color-text-secondary, #465062)',
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
  }
}

/** Above the window it was drawn in. The default sits under the app's dialogs,
 *  which is right for a menu opened on a page — but a kebab drawn INSIDE a
 *  window has to clear it, or its menu opens behind the window it was clicked
 *  in. It stays BELOW the windows its own items open (Edit, Assign, Audit
 *  trail): picking an item closes the menu, so it never has to outrank them. */
export const MENU_Z_OVER_MODAL = Z_MENU_OVER_TRAIL

export default function RowActionsMenu({ actions, zIndex = Z_ROW_MENU, horizontal = false, circled = false }: {
  actions: RowAction[]
  /** Stacking level for the portalled menu. Raise it inside a modal. */
  zIndex?: number
  /** A "⋯" trigger (default: the vertical "⋮"). */
  horizontal?: boolean
  /** The "⋮" in a ring — what a list row and a record card use, so the menu
   *  reads as a button at a glance. */
  circled?: boolean
}) {
  // A host overlay above the ladder lifts the menu with it (see layers.ts).
  const menuZ = zIndex + useContext(LayerLiftContext)
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  const MENU_WIDTH = 168
  const ROW_H = 30          // one item, incl. padding
  const MARGIN = 8

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
    // A row near the bottom of the grid used to open a menu that ran off the
    // screen — and the last items (Process trail, Delete) were simply
    // unreachable. So it opens below only when it FITS below; otherwise it
    // flips above the button, and if neither side has room it takes whichever
    // is taller and scrolls inside itself.
    const wanted = actions.length * ROW_H + MARGIN
    const below = window.innerHeight - r.bottom - MARGIN
    const above = r.top - MARGIN
    if (wanted <= below) {
      setPos({ top: r.bottom + 4, left, maxHeight: below })
    } else if (wanted <= above) {
      setPos({ top: Math.max(MARGIN, r.top - wanted - 4), left, maxHeight: above })
    } else if (above > below) {
      setPos({ top: MARGIN, left, maxHeight: above })
    } else {
      setPos({ top: r.bottom + 4, left, maxHeight: below })
    }
  }, [open, actions.length])

  if (actions.length === 0) return null

  return (
    <>
      <button
        ref={btnRef}
        className={circled ? undefined : 'btn btn-ghost btn-sm'}
        title="Actions"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        {...tw('action', 'row_menu')}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        {...(circled ? {
          onMouseEnter: () => setHover(true),
          onMouseLeave: () => setHover(false),
        } : {})}
        style={circled ? circledTrigger(open, hover) : { padding: 4 }}
      >
        {horizontal && !circled ? <MoreHorizontal size={16} /> : <MoreVertical size={circled ? 16 : 15} />}
      </button>

      {open && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: menuZ }}
          onClick={(e) => { e.stopPropagation(); setOpen(false) }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999,
              width: MENU_WIDTH, background: 'var(--color-bg-secondary, #fff)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 4,
              display: 'flex', flexDirection: 'column',
              maxHeight: pos?.maxHeight, overflowY: 'auto',
            }}
          >
            {actions.map((a, i) => (
              <button
                // Position too: an imported menu can repeat a label (Vision's
                // OBJ_ACTIONS lists Edit twice on sordamd).
                key={`${i}:${a.label}`}
                {...tw('action', a.hook || (a.kind && a.kind !== 'run' ? a.kind : a.label))}
                onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick() }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', fontSize: 14, textAlign: 'left',
                  background: 'none', border: 'none', borderRadius: 6, cursor: 'pointer',
                  color: a.danger ? 'var(--color-error, #ef4444)' : 'var(--color-text)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary, #f1f5f9)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                {a.icon}
                <span style={{ flex: 1 }}>{a.label}</span>
                {a.shortcut && <kbd style={menuKbd}>{a.shortcut}</kbd>}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
